import * as THREE from 'three';

export interface DaylightSkyConfig {
  /** Azimuth in degrees, 0 = +Z, 90 = +X. */
  readonly sunAzimuthDeg?: number;
  /** Elevation in degrees above the horizon. */
  readonly sunElevationDeg?: number;
  readonly turbidity?: number;
  readonly rayleigh?: number;
  readonly mieCoefficient?: number;
  readonly mieDirectionalG?: number;
}

export interface ResolvedDaylightSkyConfig {
  readonly sunAzimuthDeg: number;
  readonly sunElevationDeg: number;
  readonly turbidity: number;
  readonly rayleigh: number;
  readonly mieCoefficient: number;
  readonly mieDirectionalG: number;
}

export const DEFAULT_DAYLIGHT_SKY_CONFIG: ResolvedDaylightSkyConfig = Object.freeze({
  sunAzimuthDeg: 45,
  sunElevationDeg: 52,
  turbidity: 3,
  rayleigh: 1.6,
  mieCoefficient: 0.004,
  mieDirectionalG: 0.76,
});

export function resolveDaylightSkyConfig(config?: DaylightSkyConfig): ResolvedDaylightSkyConfig {
  return {
    sunAzimuthDeg: config?.sunAzimuthDeg ?? DEFAULT_DAYLIGHT_SKY_CONFIG.sunAzimuthDeg,
    sunElevationDeg: config?.sunElevationDeg ?? DEFAULT_DAYLIGHT_SKY_CONFIG.sunElevationDeg,
    turbidity: config?.turbidity ?? DEFAULT_DAYLIGHT_SKY_CONFIG.turbidity,
    rayleigh: config?.rayleigh ?? DEFAULT_DAYLIGHT_SKY_CONFIG.rayleigh,
    mieCoefficient: config?.mieCoefficient ?? DEFAULT_DAYLIGHT_SKY_CONFIG.mieCoefficient,
    mieDirectionalG: config?.mieDirectionalG ?? DEFAULT_DAYLIGHT_SKY_CONFIG.mieDirectionalG,
  };
}

export function sunDirectionFromAzimuthElevation(
  azimuthDeg: number,
  elevationDeg: number,
): THREE.Vector3 {
  const clampedElevationDeg = THREE.MathUtils.clamp(elevationDeg, -5, 90);
  const phi = THREE.MathUtils.degToRad(90 - clampedElevationDeg);
  const theta = THREE.MathUtils.degToRad(azimuthDeg);
  return new THREE.Vector3().setFromSphericalCoords(1, phi, theta).normalize();
}

export function getSunDirectionFromSkyConfig(config?: DaylightSkyConfig): THREE.Vector3 {
  const resolved = resolveDaylightSkyConfig(config);
  return sunDirectionFromAzimuthElevation(resolved.sunAzimuthDeg, resolved.sunElevationDeg);
}

export function getLightDirectionFromSunDirection(sunDirection: THREE.Vector3): THREE.Vector3 {
  return sunDirection.clone().normalize().multiplyScalar(-1);
}
